import { en, tr, type Entry, type MessageKey } from './catalogs.js';

export { type MessageKey, type Entry } from './catalogs.js';

export const catalogs = { tr, en } as const;
export type CatalogLocale = keyof typeof catalogs;

export type Params = Record<string, string | number>;

const RTL_LANGUAGES = new Set(['ar', 'fa', 'he', 'ur']);

export function isRtl(locale: string): boolean {
  return RTL_LANGUAGES.has(locale.split('-')[0] ?? locale);
}

function interpolate(template: string, params: Params | undefined): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

function pick(entry: Entry, locale: string, params: Params | undefined): string {
  if (typeof entry === 'string') return entry;
  const count = typeof params?.count === 'number' ? params.count : 0;
  const rule = new Intl.PluralRules(locale).select(count);
  return entry[rule] ?? entry.other;
}

export function createTranslator(locale: string) {
  const lang = (locale.split('-')[0] ?? 'tr') as CatalogLocale;
  const catalog = catalogs[lang] ?? catalogs.tr;
  const resolvedLocale = catalogs[lang] ? lang : 'tr';

  const t = (key: MessageKey, params?: Params): string => {
    const entry = catalog[key] ?? catalogs.tr[key];
    const formattedParams = params
      ? Object.fromEntries(
          Object.entries(params).map(([k, v]) => [
            k,
            typeof v === 'number' ? new Intl.NumberFormat(resolvedLocale).format(v) : v,
          ]),
        )
      : undefined;
    return interpolate(pick(entry, resolvedLocale, params), formattedParams);
  };

  const formatDate = (value: string | Date, options?: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(resolvedLocale, options ?? { dateStyle: 'medium', timeStyle: 'short' }).format(
      typeof value === 'string' ? new Date(value) : value,
    );

  return { t, formatDate, locale: resolvedLocale, dir: isRtl(resolvedLocale) ? 'rtl' : 'ltr' } as const;
}

export type Translator = ReturnType<typeof createTranslator>;
